<template>
  <div class="base-select" ref="wrapper" :class="{ disabled, invalid, open }">
    <!-- CONTROL -->
    <div class="base-select__control" @mousedown.prevent="onControlClick">
      <input
        ref="input"
        type="text"
        class="base-select__input"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="displayValue"
        @focus="onFocus"
        @input="onInput"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="close"
      />

      <span
        v-if="value && !disabled"
        class="base-select__clear"
        @mousedown.stop.prevent="clear"
      >
        ×
      </span>

      <span class="base-select__arrow" :class="{ open }">▾</span>
    </div>

    <!-- DROPDOWN -->
    <ul v-show="open && !disabled" class="base-select__menu">
      <li
        v-for="(item, i) in filteredOptions"
        :key="i"
        class="base-select__option"
        :class="{ active: i === activeIndex }"
        @mouseenter="activeIndex = i"
        @mousedown.prevent="select(item)"
      >
        {{ getLabel(item) }}
      </li>

      <li v-if="!options.length" class="base-select__empty">Tidak ada data</li>
    </ul>
  </div>
</template>

<script>
export default {
  /* ✅ KONTRAK V-MODEL VUE 2 */
  model: {
    prop: "value",
    event: "input",
  },

  props: {
    value: Object, // v-model
    options: { type: Array, default: () => [] },
    labelKey: String,
    labelFn: Function,
    placeholder: { type: String, default: "-- Cari & Pilih --" },
    disabled: Boolean,
    invalid: Boolean,
    searchFn: Function,
  },

  data() {
    return {
      open: false,
      searchText: "",
      isTyping: false,
      activeIndex: -1,
    };
  },

  computed: {
    displayValue() {
      if (this.isTyping) return this.searchText;
      if (this.value) return this.getLabel(this.value);
      return "";
    },
    filteredOptions() {
      if (!this.searchText) return this.options;

      const keyword = this.searchText.toLowerCase();

      return this.options.filter((item) =>
        String(this.getLabel(item)).toLowerCase().includes(keyword)
      );
    },
  },

  watch: {
    value() {
      this.isTyping = false;
      this.searchText = "";
      this.syncActive();
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.onClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener("mousedown", this.onClickOutside);
  },

  methods: {
    getLabel(item) {
      if (!item) return "";

      // ✅ PRIORITAS CUSTOM LABEL
      if (this.labelFn) {
        return this.labelFn(item);
      }

      // fallback default
      return this.labelKey ? item[this.labelKey] : item;
    },

    getKey(item) {
      return item;
    },

    onControlClick() {
      if (this.disabled) return;

      this.open = !this.open; // ✅ toggle

      if (this.open) {
        this.$refs.input.focus();
        this.syncActive();
      }
    },

    onFocus() {
      if (this.disabled) return;

      // hanya buka kalau belum open
      if (!this.open) {
        this.open = true;
        this.syncActive();
      }
    },

    onInput(e) {
      this.searchText = e.target.value;
      this.isTyping = true;
      this.activeIndex = -1;
      this.open = true;

      if (this.searchFn) {
        this.searchFn(this.searchText);
      }
    },

    select(item) {
      this.isTyping = false;
      this.searchText = "";

      this.$emit("input", item); // ✅ VUE 2

      this.open = false;
      this.activeIndex = -1;
    },

    clear() {
      this.$emit("input", null);
      this.$emit("clear");

      this.searchText = "";
      this.isTyping = false;
      this.activeIndex = -1;
      this.open = false;
    },

    close() {
      this.open = false;
      this.isTyping = false;
      this.searchText = "";
    },

    onArrowDown() {
      if (!this.open) {
        this.open = true;
        this.activeIndex = 0;
        return;
      }

      if (this.activeIndex < this.filteredOptions.length - 1) {
        this.activeIndex++;
      }

      this.scrollToActive();
    },

    onArrowUp() {
      if (this.activeIndex > 0) {
        this.activeIndex--;
        this.scrollToActive();
      }
    },

    onEnter() {
      // kalau dropdown belum kebuka → buka
      if (!this.open) {
        this.open = true;
        this.syncActive();
        return;
      }

      // 1️⃣ ada item aktif → pilih itu
      if (this.activeIndex >= 0) {
        this.select(this.filteredOptions[this.activeIndex]);
        return;
      }

      // 2️⃣ tidak ada activeIndex tapi sudah ada value → pilih ulang value
      if (this.value) {
        this.select(this.value);
      }
    },

    scrollToActive() {
      this.$nextTick(() => {
        const menu = this.$el.querySelector(".base-select__menu");
        const active = menu?.querySelector(".base-select__option.active");
        if (active) {
          active.scrollIntoView({ block: "nearest" });
        }
      });
    },

    syncActive() {
      if (!this.value) {
        // ✅ BUKAN modelValue
        this.activeIndex = -1;
        return;
      }

      this.activeIndex = this.filteredOptions.findIndex(
        (o) => o === this.value
      );
    },

    onClickOutside(e) {
      if (!this.$refs.wrapper.contains(e.target)) {
        this.close();
      }
    },
  },
};
</script>

<style scoped>
.base-select {
  position: relative;
}

.base-select__control {
  position: relative;
  display: flex;
  align-items: center;
}

.base-select__input {
  width: 100%;
  height: 38px; /* 🔥 v-select height */
  padding: 6px 44px 6px 12px;
  font-size: 14px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  outline: none;
  background-color: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

/* 🔥 focus (v-select style) */
.base-select.open .base-select__input,
.base-select__input:focus {
  border-color: #2684ff;
  box-shadow: 0 0 0 1px #2684ff;
}

.base-select.disabled .base-select__input {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.base-select.invalid .base-select__input {
  border-color: #dc3545;
}

.base-select.invalid.open .base-select__input {
  box-shadow: 0 0 0 1px #dc3545;
}

.base-select__menu {
  position: absolute;
  z-index: 1050;
  width: 100%;
  margin-top: 4px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 11px rgba(0, 0, 0, 0.1); /* 🔥 penting */
}

.base-select__option {
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.base-select__option:hover {
  background: #f8f9fa;
}

.base-select__option.active {
  background: #2684ff;
  color: #fff;
}

.base-select__clear:hover {
  color: #495057;
}

.base-select__clear {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #adb5bd;
  cursor: pointer;
}

.base-select__arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%) rotate(0deg);
  font-size: 18px;
  color: #6c757d;
  pointer-events: none;
  transition: transform 0.15s ease;
}

.base-select.open .base-select__arrow {
  transform: translateY(-50%) rotate(180deg);
}

.base-select__input::placeholder {
  color: #6c757d;
}

.base-select__empty {
  padding: 10px;
  font-size: 13px;
  color: #6c757d;
  text-align: center;
}
</style>
